import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import {
  FaCalendarAlt,
  FaChartLine,
  FaFilter,
  FaList,
  FaRedoAlt,
  FaUsers,
  FaWallet,
} from 'react-icons/fa';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import Navigation from './Navigation';
import './MembershipPaymentStatistics.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const currencyFormatter = new Intl.NumberFormat('id-ID');

const emptySummary = {
  total_periods: 0,
  total_transactions: 0,
  total_members: 0,
  total_amount: 0,
  success_count: 0,
  pending_count: 0,
  failed_count: 0,
};

function buildStatisticsRange(view, preset) {
  const now = new Date();
  const format = value => value.toISOString().split('T')[0];
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (view === 'monthly_membership') {
    switch (preset) {
      case 'this-year': {
        const start = new Date(today.getFullYear(), 0, 1);
        return { dateFrom: format(start), dateTo: format(today) };
      }
      case 'last-12-months': {
        const start = new Date(today.getFullYear(), today.getMonth() - 11, 1);
        return { dateFrom: format(start), dateTo: format(today) };
      }
      default:
        return { dateFrom: '', dateTo: '' };
    }
  }

  switch (preset) {
    case 'today':
      return { dateFrom: format(today), dateTo: format(today) };
    case 'this-week': {
      const start = new Date(today);
      const day = start.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      start.setDate(start.getDate() + diff);
      return { dateFrom: format(start), dateTo: format(today) };
    }
    case 'last-30-days': {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { dateFrom: format(start), dateTo: format(today) };
    }
    default:
      return { dateFrom: '', dateTo: '' };
  }
}

function MembershipPaymentStatistics({ view = 'daily_member' }) {
  const navigate = useNavigate();
  const isMonthly = view === 'monthly_membership';
  const defaultQuickRange = isMonthly ? 'last-12-months' : 'last-30-days';
  const defaultRange = buildStatisticsRange(view, defaultQuickRange);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [appliedFilters, setAppliedFilters] = useState(defaultRange);
  const [quickRange, setQuickRange] = useState(defaultQuickRange);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);

  const title = isMonthly ? 'Statistik Membership Bulanan' : 'Statistik Member Harian';
  const eyebrow = isMonthly ? 'Monthly performance' : 'Daily performance';
  const description = isMonthly
    ? 'Pantau performa membership bulanan berdasarkan periode, jumlah transaksi, kontribusi revenue, dan distribusi status pembayaran.'
    : 'Pantau aktivitas member harian berdasarkan periode, jumlah member unik, nominal transaksi, dan distribusi status pembayaran.';
  const formatCurrency = amount => `Rp ${currencyFormatter.format(Number(amount || 0))}`;

  const fetchStatistics = useCallback(async (page = currentPage, perPage = rowsPerPage, filters = appliedFilters) => {
    setLoading(true);
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      setLoading(false);
      navigate('/loginadmin');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}membership-payment/statistics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          view,
          limit: perPage,
          offset: (page - 1) * perPage,
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
        }),
      });

      const data = await response.json();
      if (data.error === 0 && Array.isArray(data.results)) {
        setRows(data.results);
        setSummary(data.summary || emptySummary);
        setTotalRows(Number(data.total || 0));
        return;
      }

      setRows([]);
      setSummary(emptySummary);
      setTotalRows(0);
      Swal.fire('Error', data.message || 'Failed to fetch statistics', 'error');
    } catch (error) {
      console.error('Statistics fetch error:', error);
      setRows([]);
      setSummary(emptySummary);
      setTotalRows(0);
      Swal.fire('Error', 'Failed to fetch statistics', 'error');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, currentPage, navigate, rowsPerPage, view]);

  useEffect(() => {
    fetchStatistics(currentPage, rowsPerPage, appliedFilters);
  }, [appliedFilters, currentPage, fetchStatistics, rowsPerPage]);

  const applyFilter = () => {
    setCurrentPage(1);
    setAppliedFilters({ dateFrom, dateTo });
  };

  const resetFilters = () => {
    setDateFrom(defaultRange.dateFrom);
    setDateTo(defaultRange.dateTo);
    setQuickRange(defaultQuickRange);
    setCurrentPage(1);
    setAppliedFilters(defaultRange);
  };

  const applyQuickRange = preset => {
    const range = buildStatisticsRange(view, preset);
    setQuickRange(preset);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
    setCurrentPage(1);
    setAppliedFilters(range);
  };

  const summaryCards = [
    {
      key: 'amount',
      label: 'Total revenue',
      value: formatCurrency(summary.total_amount),
      icon: FaWallet,
    },
    {
      key: 'transactions',
      label: 'Total transaksi',
      value: summary.total_transactions,
      icon: FaList,
    },
    {
      key: 'members',
      label: 'Member unik',
      value: summary.total_members,
      icon: FaUsers,
    },
    {
      key: 'success',
      label: 'Status sukses',
      value: summary.success_count,
      icon: FaChartLine,
    },
  ];

  const columns = [
    {
      name: 'Period',
      selector: row => row.period_key,
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="payment-stats-table__period">
          <strong>{row.period_label}</strong>
          <small>{row.period_key}</small>
        </span>
      ),
    },
    {
      name: 'Transactions',
      selector: row => row.total_transactions,
      sortable: true,
    },
    {
      name: 'Members',
      selector: row => row.total_members,
      sortable: true,
    },
    {
      name: 'Revenue',
      selector: row => row.total_amount,
      sortable: true,
      cell: row => (
        <span className="payment-stats-table__amount">
          {formatCurrency(row.total_amount)}
        </span>
      ),
    },
    {
      name: 'Success',
      selector: row => row.success_count,
      sortable: true,
    },
    {
      name: 'Pending',
      selector: row => row.pending_count,
      sortable: true,
    },
    {
      name: 'Failed',
      selector: row => row.failed_count,
      sortable: true,
    },
  ];

  const quickOptions = isMonthly
    ? [
        { key: 'this-year', label: 'Tahun ini' },
        { key: 'last-12-months', label: '12 bulan' },
      ]
    : [
        { key: 'today', label: 'Hari ini' },
        { key: 'this-week', label: 'Minggu ini' },
        { key: 'last-30-days', label: '30 hari' },
      ];

  return (
    <>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaChartLine}
          eyebrow={eyebrow}
          title={title}
          description={description}
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: isMonthly ? 'Membership Bulanan Statistics' : 'Member Harian Statistics' },
          ]}
          actions={(
            <Button variant="secondary" onClick={() => navigate('/membership-payment')}>
              <FaWallet /> Open Membership Payments
            </Button>
          )}
          meta={(
            <span className="soft-stat">
              <FaList /> {totalRows} periods
            </span>
          )}
        />

        <section className="payment-stats-summary-grid">
          {summaryCards.map(card => {
            const Icon = card.icon;
            return (
              <article key={card.key} className="surface-card payment-stats-summary-card">
                <span className="payment-stats-summary-card__icon">
                  <Icon />
                </span>
                <div className="payment-stats-summary-card__copy">
                  <p>{card.label}</p>
                  <strong>{card.value}</strong>
                </div>
              </article>
            );
          })}
        </section>

        <div className="section-card table-card payment-stats-shell">
          <div className="payment-stats-filter-panel">
            <div className="payment-stats-filter-panel__header">
              <div>
                <span className="page-eyebrow">Statistics filter</span>
                <h3>Review by date range</h3>
              </div>

              <div className="payment-stats-quick-range">
                {quickOptions.map(option => (
                  <button
                    key={option.key}
                    type="button"
                    className={`payment-stats-quick-range__chip ${quickRange === option.key ? 'is-active' : ''}`}
                    onClick={() => applyQuickRange(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="toolbar-grid payment-stats-filter-grid">
              <Form.Group className="field-stack span-4">
                <Form.Label>Date from</Form.Label>
                <div className="control-with-icon">
                  <span className="control-with-icon__icon">
                    <FaCalendarAlt />
                  </span>
                  <Form.Control
                    type="date"
                    value={dateFrom}
                    onChange={event => {
                      setQuickRange('custom');
                      setDateFrom(event.target.value);
                    }}
                  />
                </div>
              </Form.Group>

              <Form.Group className="field-stack span-4">
                <Form.Label>Date to</Form.Label>
                <div className="control-with-icon">
                  <span className="control-with-icon__icon">
                    <FaCalendarAlt />
                  </span>
                  <Form.Control
                    type="date"
                    value={dateTo}
                    onChange={event => {
                      setQuickRange('custom');
                      setDateTo(event.target.value);
                    }}
                  />
                </div>
              </Form.Group>

              <div className="payment-stats-filter-actions span-4">
                <Button onClick={applyFilter}>
                  <FaFilter /> Apply
                </Button>
                <Button variant="secondary" onClick={resetFilters}>
                  <FaRedoAlt /> Reset
                </Button>
              </div>
            </div>
          </div>

          <div className="payment-stats-table-head">
            <div>
              <span className="page-eyebrow">Records</span>
              <h3>{isMonthly ? 'Monthly membership performance' : 'Daily member performance'}</h3>
              <p>Data statistik disusun per periode agar evaluasi penjualan dan volume member lebih mudah ditindaklanjuti.</p>
            </div>
            <span className="soft-stat">
              <FaList /> {summary.total_periods} grouped periods
            </span>
          </div>

          <div className="card-body pt-0">
            <AdminDataTable
              columns={columns}
              data={rows}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={rowsPerPage}
              paginationRowsPerPageOptions={[10, 20, 50]}
              onChangePage={page => setCurrentPage(page)}
              onChangeRowsPerPage={(nextRowsPerPage, page) => {
                setRowsPerPage(nextRowsPerPage);
                setCurrentPage(page);
              }}
              progressPending={loading}
              highlightOnHover
              pointerOnHover
              emptyTitle="No statistics found"
              emptyDescription="Belum ada data statistik pada rentang tanggal yang dipilih."
            />
          </div>
        </div>
      </Container>
    </>
  );
}

export default MembershipPaymentStatistics;
